import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { AssistantPageContext } from '@almanac/contracts';
import { matchPath, useLocation } from 'react-router-dom';
import { useMe } from '../../api/MeContext';
import { useAuth } from '../../auth/AuthContext';
import {
  loadActiveConversationId,
  loadConversations,
  setActiveConversationId,
  type StoredConversation,
} from '../../lib/assistant-store';
import { ALMANAC_ASK, type AskAssistantDetail } from '../../lib/ask-assistant';
import { AssistantDock } from './AssistantDock';

export interface AssistantSeed {
  question: string;
  researchId?: string;
  nonce: number;
}

interface AssistantContextValue {
  open: boolean;
  activeConversationId: string;
  activeConversation: StoredConversation;
  recentConversations: StoredConversation[];
  groundingResearchId: string | null;
  pageContext: AssistantPageContext | undefined;
  seed: AssistantSeed | null;
  /**
   * localStorage scope for conversation persistence — the authenticated
   * user's sub, or null until it resolves (persistence is skipped so one
   * account's transcripts can never land under another's key).
   */
  storageScope: string | null;
  close: () => void;
  newConversation: () => void;
  switchConversation: (id: string) => void;
  refreshConversations: () => void;
}

const AssistantContext = createContext<AssistantContextValue | null>(null);

/** Create an id for a local assistant conversation. */
function freshConversationId(): string {
  return crypto.randomUUID();
}

/** Build the transient shape used before a conversation has persisted messages. */
function createEmptyConversation(
  id = freshConversationId(),
  researchId?: string,
): StoredConversation {
  const now = new Date().toISOString();
  return {
    id,
    title: 'Untitled',
    researchId,
    messages: [],
    createdAt: now,
    updatedAt: now,
  };
}

/** Extract assistant grounding from authenticated research routes. */
function routeResearchId(pathname: string): string | null {
  const posterMatch = matchPath('/research/:researchId/poster', pathname);
  if (posterMatch?.params.researchId) return posterMatch.params.researchId;
  const researchMatch = matchPath('/research/:researchId', pathname);
  return researchMatch?.params.researchId ?? null;
}

/** Route identifiers are hints only; the API rechecks access before retrieving content. */
export function routePageContext(pathname: string): AssistantPageContext | undefined {
  const researchId = routeResearchId(pathname);
  if (researchId) return { research_id: researchId };
  const docSlug = matchPath('/docs/:docSlug', pathname)?.params.docSlug;
  if (docSlug) return { doc_slug: docSlug };
  const packageSlug = matchPath('/briefing/packages/:packageSlug', pathname)?.params.packageSlug;
  if (packageSlug) return { package_slug: packageSlug };
  const digestDate = matchPath('/digests/:date', pathname)?.params.date;
  if (digestDate) {
    return { digest_date: digestDate };
  }
  return undefined;
}

/** Decide whether the dock should be visible for the current auth and route. */
function shouldRenderDock(pathname: string, status: ReturnType<typeof useAuth>['status']): boolean {
  if (status !== 'authenticated') return false;
  if (pathname === '/login') return false;
  if (pathname === '/auth/callback') return false;
  if (pathname.startsWith('/p/')) return false;
  return true;
}

/** Global assistant state, conversation persistence, and route grounding. */
export function AssistantProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [eventResearchId, setEventResearchId] = useState<string | null>(null);
  const [activeConversationId, setActiveConversationIdState] = useState(freshConversationId);
  const [recentConversations, setRecentConversations] = useState<StoredConversation[]>([]);
  const [seed, setSeed] = useState<AssistantSeed | null>(null);
  const nonceRef = useRef(0);
  const hydratedScope = useRef<string | null>(null);

  const pathResearchId = routeResearchId(location.pathname);
  const groundingResearchId = eventResearchId ?? pathResearchId;
  const pageContext = useMemo(() => routePageContext(location.pathname), [location.pathname]);

  useEffect(() => {
    setEventResearchId(null);
  }, [location.pathname]);

  // Storage is scoped by the authenticated user's sub, read from the shared
  // /api/me fetch. Until it resolves the dock works in-memory only.
  const me = useMe();
  const storageScope = me?.sub ?? null;

  // Hydrate the working memory once the scope is known.
  useEffect(() => {
    if (!storageScope || hydratedScope.current === storageScope) return;
    hydratedScope.current = storageScope;
    setRecentConversations(loadConversations(storageScope));
    const storedActive = loadActiveConversationId(storageScope);
    if (storedActive) setActiveConversationIdState(storedActive);
  }, [storageScope]);

  useEffect(() => {
    if (!storageScope) return;
    setActiveConversationId(storageScope, activeConversationId);
  }, [activeConversationId, storageScope]);

  const refreshConversations = useCallback(() => {
    if (!storageScope) return;
    setRecentConversations(loadConversations(storageScope));
  }, [storageScope]);

  useEffect(() => {
    if (open) refreshConversations();
  }, [open, refreshConversations]);

  const switchConversation = useCallback(
    (id: string) => {
      setActiveConversationIdState(id);
      // A pending seed must not replay into the conversation being switched to
      // (the panel remounts per conversation, resetting its consumed-nonce guard).
      setSeed(null);
      setOpen(true);
      refreshConversations();
    },
    [refreshConversations],
  );

  const newConversation = useCallback(() => {
    setActiveConversationIdState(freshConversationId());
    setSeed(null);
    setOpen(true);
    refreshConversations();
  }, [refreshConversations]);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    function onAsk(event: Event) {
      const detail = (event as CustomEvent<AskAssistantDetail>).detail ?? {};
      if (detail.researchId) setEventResearchId(detail.researchId);
      nonceRef.current += 1;
      setSeed({
        question: detail.question ?? '',
        researchId: detail.researchId,
        nonce: nonceRef.current,
      });
      setOpen(true);
    }
    window.addEventListener(ALMANAC_ASK, onAsk);
    return () => window.removeEventListener(ALMANAC_ASK, onAsk);
  }, []);

  const activeConversation = useMemo(() => {
    const stored = recentConversations.find(
      (conversation) => conversation.id === activeConversationId,
    );
    return stored ?? createEmptyConversation(activeConversationId);
  }, [activeConversationId, recentConversations]);

  const value = useMemo<AssistantContextValue>(
    () => ({
      open,
      activeConversationId,
      activeConversation,
      recentConversations,
      groundingResearchId,
      pageContext,
      seed,
      storageScope,
      close,
      newConversation,
      switchConversation,
      refreshConversations,
    }),
    [
      activeConversation,
      activeConversationId,
      close,
      groundingResearchId,
      pageContext,
      newConversation,
      open,
      recentConversations,
      refreshConversations,
      seed,
      storageScope,
      switchConversation,
    ],
  );

  const renderDock = shouldRenderDock(location.pathname, status);

  return (
    <AssistantContext.Provider value={value}>
      {children}
      {renderDock ? <AssistantDock /> : null}
    </AssistantContext.Provider>
  );
}

/** Read global assistant state from the provider. */
export function useAssistant(): AssistantContextValue {
  const value = useContext(AssistantContext);
  if (!value) throw new Error('useAssistant must be used inside AssistantProvider');
  return value;
}
